import { useState, useEffect } from "react";
import { Form, Select, Button, Input, Radio } from "antd";
import { useTranslation } from "../../../../utils/onboardingI18n";
import { useSelector } from "react-redux";
import {
  addUpdateFinancialInfo,
  getFinancialInfo,
  getIndustryTypes,
} from "../../../../api/request/individualOnboarding";
import { handleApiError } from "../../../../utils/handleApiError.js";
import toast from "react-hot-toast";
import Loader from "../../../../components/Loader.jsx";
import {
  DEPOSIT_METHOD_OPTIONS,
  DEPOSIT_METHOD_OTHER,
  SOURCE_OF_WEALTH_OPTIONS,
  SOURCE_OF_WEALTH_OTHER,
} from "../../../../utils/onboardingEnums.js";
import OnboardingMultiSelect from "../../../../components/onboarding/OnboardingMultiSelect.jsx";
const { TextArea } = Input;


const FinancialInfo = ({ onNext, onPrevious, isSubmitted, id }) => {
  const { t } = useTranslation("onBoarding");
  const { userDetails } = useSelector((state) => state.user);
  const [form] = Form.useForm();
  const [employmentStatus, setEmploymentStatus] = useState("Employed");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [industryOptions, setIndustryOptions] = useState([]);
  const [industryTypesData, setIndustryTypesData] = useState([]);

  const isDisabled = isSubmitted;

  const formEmploymentStatus = Form.useWatch("employmentStatus", form);
  const occupationRole = Form.useWatch("occupationRole", form);
  const industry = Form.useWatch("industry", form);
  const previousIndustry = Form.useWatch("previousIndustry", form);
  const subjectOfStudy = Form.useWatch("subjectOfStudy", form);
  const sourceOfWealth = Form.useWatch("sourceOfWealth", form);
  const depositMethod = Form.useWatch("depositMethod", form);

  const sourceOfWealthOptions = SOURCE_OF_WEALTH_OPTIONS;
  const depositMethodOptions = DEPOSIT_METHOD_OPTIONS;

  useEffect(() => {
    if (formEmploymentStatus) {
      setEmploymentStatus(formEmploymentStatus);
    } else {
      setEmploymentStatus("Employed");
    }
  }, [formEmploymentStatus]);

  const employmentStatusOptions = [
    { value: "Employed", label: "Employed" },
    { value: "Self Employed", label: "Self Employed" },
    { value: "Retired", label: "Retired" },
    { value: "Not Employed", label: "Not Employed" },
    { value: "Student", label: "Student" },
  ];

  const handleEmploymentStatusChange = (value) => {
    setEmploymentStatus(value);
  };

  const occupationRoleOptions = [
    { value: "HOD", label: "HOD" },
    { value: "CEO / Founder / VP", label: "CEO / Founder / VP" },
    { value: "Senior Manager", label: "Senior Manager" },
    { value: "Director", label: "Director" },
    { value: "Others", label: "Others" },
  ];

  // Fetch industry types from API
  const fetchIndustryTypes = async () => {
    try {
      const { data } = await getIndustryTypes();
      if (data?.success && data?.data) {
        // Map API response to dropdown options (using name as value and label)
        const options = data.data.map((item) => ({
          value: item.name,
          label: item.name,
        }));
        setIndustryOptions(options);
        setIndustryTypesData(data.data);
      }
    } catch (error) {
      console.error("Error fetching industry types:", error);
    }
  };

  useEffect(() => {
    fetchIndustryTypes();
  }, []);

  const valueOfSavingsOptions = [
    { value: "$5,000 - $49,999", label: "$5,000 - $49,999" },
    { value: "$50,000 - $99,999", label: "$50,000 - $99,999" },
    { value: "$100,000 - $499,999", label: "$100,000 - $499,999" },
    { value: "$500,000 - $999,999", label: "$500,000 - $999,999" },
    { value: "$1,000,000 or more", label: "$1,000,000 or more" },
  ];

  const annualIncomeOptions = [
    { value: "<$25,000", label: "<$25,000" },
    { value: "$25,000-$100,000", label: "$25,000-$100,000" },
    { value: "$100,001-$500,000", label: "$100,001-$500,000" },
    { value: "$500,001-$1,500,000", label: "$500,001-$1,500,000" },
    { value: "$1,500,001-$5,000,000", label: "$1,500,001-$5,000,000" },
    { value: "$5,000,001-$10,000,000", label: "$5,000,001-$10,000,000" },
    { value: "$10,000,000 or more", label: "$10,000,000 or more" },
  ];

  const netWorthOptions = [
    { value: "<$25,000", label: "<$25,000" },
    { value: "$25,000-$100,000", label: "$25,000-$100,000" },
    { value: "$100,001-$500,000", label: "$100,001-$500,000" },
    { value: "$500,001-$1,500,000", label: "$500,001-$1,500,000" },
    { value: "$1,500,001-$5,000,000", label: "$1,500,001-$5,000,000" },
    { value: "$5,000,001-$10,000,000", label: "$5,000,001-$10,000,000" },
    { value: "$10,000,000 or more", label: "$10,000,000 or more" },
  ];

  const getSourceOfWealthIds = (sources) => {
    if (!Array.isArray(sources)) return [];
    return sources.filter((source) => Number(source) > 0);
  };

  const subjectOfStudyOptions = [
    { value: "Business", label: "Business" },
    { value: "Engineering", label: "Engineering" },
    { value: "Medicine", label: "Medicine" },
    { value: "Law", label: "Law" },
    { value: "Arts", label: "Arts" },
    { value: "Science", label: "Science" },
    { value: "Technology", label: "Technology" },
    { value: "Finance", label: "Finance" },
    { value: "Economics", label: "Economics" },
    { value: "Education", label: "Education" },
    { value: "Others", label: "Others" },
  ];

  const getEmploymentStatusId = (status) => {
    const mapping = {
      Employed: 1,
      "Self Employed": 2,
      Retired: 3,
      "Not Employed": 4,
      Student: 5,
    };
    return mapping[status] || 0;
  };

  const getOccupationRoleId = (role) => {
    const mapping = {
      HOD: 1,
      "CEO / Founder / VP": 2,
      "Senior Manager": 3,
      Director: 4,
      Others: 5,
    };
    return mapping[role] || 0;
  };

  // Get industry ID from name
  const getIndustryId = (industryName) => {
    if (!industryName) return 0;
    const industryType = industryTypesData.find((item) => item.name === industryName);
    return industryType?.id || 0;
  };

  const getEmploymentStatusFromId = (id) => {
    const mapping = {
      1: "Employed",
      2: "Self Employed",
      3: "Retired",
      4: "Not Employed",
      5: "Student",
    };
    return mapping[id] || null;
  };

  const getOccupationRoleFromId = (id) => {
    const mapping = {
      1: "HOD",
      2: "CEO / Founder / VP",
      3: "Senior Manager",
      4: "Director",
      5: "Others",
    };
    return mapping[id] || null;
  };

  const getIndustryFromId = (id) => {
    if (!id || id < 1) return null;
    const industryType = industryTypesData.find((item) => item.id === id);
    return industryType?.name || null;
  };

  const getSourceOfWealthFromIds = (ids) => {
    if (!Array.isArray(ids)) return [];
    return ids.filter((id) => Number(id) > 0);
  };

  const renderSourceFundsAndDepositFields = () => (
    <>
      <Form.Item
        label={<span>{t("Source of Funds and Wealth")}</span>}
        name="sourceOfWealth"
        rules={[
          {
            required: true,
            message: t("Please select source of funds and wealth"),
          },
        ]}
        className="md:col-span-2"
      >
        <OnboardingMultiSelect
          placeholder={t("Select")}
          options={sourceOfWealthOptions}
          disabled={isDisabled}
        />
      </Form.Item>
      {Array.isArray(sourceOfWealth) &&
        sourceOfWealth.includes(SOURCE_OF_WEALTH_OTHER) && (
          <Form.Item
            label={<span>{t("Please specify other source of funds and wealth")}</span>}
            name="otherSourceOfWealth"
            rules={[
              {
                required: true,
                message: t("Please specify other source of funds and wealth"),
              },
            ]}
            className="md:col-span-2"
          >
            <Input
              placeholder={t("Enter other source of funds and wealth")}
              disabled={isDisabled}
            />
          </Form.Item>
        )}
      <Form.Item
        label={<span>{t("Deposit Method")}</span>}
        name="depositMethod"
        rules={[
          { required: true, message: t("Please select deposit method") },
        ]}
        className="md:col-span-2"
      >
        <Select
          placeholder={t("Select")}
          options={depositMethodOptions}
          disabled={isDisabled}
        />
      </Form.Item>
      {depositMethod === DEPOSIT_METHOD_OTHER && (
        <Form.Item
          label={<span>{t("Please specify other deposit method")}</span>}
          name="otherDepositMethod"
          rules={[
            {
              required: true,
              message: t("Please specify other deposit method"),
            },
          ]}
          className="md:col-span-2"
        >
          <Input
            placeholder={t("Enter other deposit method")}
            disabled={isDisabled}
          />
        </Form.Item>
      )}
    </>
  );

  const loadFinancialInfo = async () => {
    if (!id) return;

    try {
      setIsLoading(true);
      const { data } = await getFinancialInfo(id);
      if (data?.success && data?.data) {
        const financialInfo = data.data;

        const employmentStatusValue = getEmploymentStatusFromId(
          financialInfo.employeeStatus
        );

        const occupationRoleValue = getOccupationRoleFromId(
          financialInfo.employmentOccupationRole
        );

        const industryValue = getIndustryFromId(financialInfo.industryTypeId);

        const previousIndustryValue = getIndustryFromId(
          financialInfo.previousIndustryTypeId
        );

        const sourceOfWealthValues = getSourceOfWealthFromIds(
          financialInfo.sourceOfWealth
        );

        form.setFieldsValue({
          employmentStatus: employmentStatusValue || "Employed",
          annualIncome: financialInfo.annualIncome || "",
          valueOfSavings: financialInfo.savingsValue || "",
          sourceOfWealth: sourceOfWealthValues,
          otherSourceOfWealth: financialInfo.otherSourceOfWealth || "",
          depositMethod: financialInfo.depositMethod || undefined,
          otherDepositMethod: financialInfo.otherDepositMethod || "",
          netWorth: financialInfo.netWorth || "",
          tradingFrequency: financialInfo.tradingExperience || "",
          understandingLevel: financialInfo.understandingLevel || "",
          employerAddress: financialInfo.employerAddress || "",
          employerName: financialInfo.employerName || "",
          industry: industryValue || undefined,
          occupationRole: occupationRoleValue || undefined,
          companyName: financialInfo.companyName || "",
          previousIndustry: previousIndustryValue || undefined,
          institution: financialInfo.institutionName || "",
          subjectOfStudy: financialInfo.studyOfSubject || "",
        });

        if (employmentStatusValue) {
          setEmploymentStatus(employmentStatusValue);
        }
      }
    } catch (error) {
      console.error("Error loading financial info:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (id && industryTypesData.length > 0) {
      loadFinancialInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, industryTypesData]);

  const handleSubmit = async (values) => {
    if (isDisabled) return;
    try {
      setIsSubmitLoading(true);

      const payload = {
        // userId: userDetails?._id || "",
        employeeStatus: getEmploymentStatusId(values.employmentStatus),
        annualIncome: values.annualIncome || "",
        savingsValue: values.valueOfSavings || "",
        sourceOfWealth: getSourceOfWealthIds(values.sourceOfWealth),
        depositMethod: values.depositMethod,
        netWorth: values.netWorth || "",
        tradingExperience: values.tradingFrequency || "",
        understandingLevel: values.understandingLevel || "",
        employerAddress: values.employerAddress || "",
        employerName: values.employerName || "",
        industryTypeId: values.industry
          ? getIndustryId(values.industry)
          : 0,
        employmentOccupationRole: values.occupationRole
          ? getOccupationRoleId(values.occupationRole)
          : 0,
        companyName: values.companyName || "",
        previousIndustryTypeId: values.previousIndustry
          ? getIndustryId(values.previousIndustry)
          : 0,
        institutionName: values.institution || "",
        studyOfSubject: values.subjectOfStudy || "",
      };
      if (id) {
        payload.id = id;
      }

      if (
        Array.isArray(values.sourceOfWealth) &&
        values.sourceOfWealth.includes(SOURCE_OF_WEALTH_OTHER) &&
        values.otherSourceOfWealth
      ) {
        payload.otherSourceOfWealth = values.otherSourceOfWealth;
      }

      if (values.depositMethod === DEPOSIT_METHOD_OTHER && values.otherDepositMethod) {
        payload.otherDepositMethod = values.otherDepositMethod;
      }

      // Rule 1: EmployeeStatus = Employed OR SelfEmployed AND Industry = "Other" -> OtherIndustryName required
      if (
        (values.employmentStatus === "Employed" ||
          values.employmentStatus === "Self Employed") &&
        values.industry === "Other" &&
        values.industryOther
      ) {
        payload.OtherIndustryName = values.industryOther;
      }

      // Rule 2: EmployeeStatus = Retired AND PreviousIndustry = "Other" -> OtherPreviousIndustryName required
      if (
        values.employmentStatus === "Retired" &&
        values.previousIndustry === "Other" &&
        values.previousIndustryOther
      ) {
        payload.OtherPreviousIndustryName = values.previousIndustryOther;
      }

      // Rule 3: EmployeeStatus = Employed OR Retired AND EmploymentOccupationRole = "Other" -> OtherOccupationRole required
      if (
        (values.employmentStatus === "Employed" ||
          values.employmentStatus === "Retired") &&
        values.occupationRole === "Others" &&
        values.occupationRoleOther
      ) {
        payload.OtherOccupationRole = values.occupationRoleOther;
      }

      if (values.subjectOfStudy === "Others" && values.subjectOfStudyOther) {
        payload.studyOfSubject = values.subjectOfStudyOther;
      } else if (values.subjectOfStudy) {
        payload.studyOfSubject = values.subjectOfStudy;
      }

      const { data } = await addUpdateFinancialInfo(payload);

      if (data?.success) {
        toast.success(
          data?.message || t("Financial information saved successfully")
        );
        onNext();
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsSubmitLoading(false);
    }
  };

  const renderEmploymentStatusForm = () => {
    if (!employmentStatus) {
      return (
        <div className="text-center py-12 text-gray-500">
          <p>{t("Please select your employment status to continue.")}</p>
        </div>
      );
    }

    if (employmentStatus === "Employed") {
      return (
        <div className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item
              label={<span>{t("Address of Employer")}</span>}
              name="employerAddress"
              rules={[
                {
                  required: true,
                  message: t("Please enter employer address"),
                },
              ]}
              className="md:col-span-2"
            >
              <TextArea
                rows={4}
                placeholder={t("Enter employer address")}
                disabled={isDisabled}
              />
            </Form.Item>
            <Form.Item
              label={<span>{t("Name of Employer")}</span>}
              name="employerName"
              rules={[
                { required: true, message: t("Please enter employer name") },
              ]}
            >
              <Input placeholder={t("Enter employer name")} disabled={isDisabled} />
            </Form.Item>
            <Form.Item
              label={<span>{t("Occupation Role")}</span>}
              name="occupationRole"
              rules={[
                { required: true, message: t("Please select occupation role") },
              ]}
            >
              <Select
                placeholder={t("Select")}
                options={occupationRoleOptions}
                disabled={isDisabled}
              />
            </Form.Item>
            {occupationRole === "Others" && (
              <Form.Item
                label={<span>{t("Please specify occupation role")}</span>}
                name="occupationRoleOther"
                rules={[
                  {
                    required: true,
                    message: t("Please specify occupation role"),
                  },
                ]}
              >
                <Input
                  placeholder={t("Enter occupation role")}
                  disabled={isDisabled}
                />
              </Form.Item>
            )}
            <Form.Item
              label={<span>{t("Industry")}</span>}
              name="industry"
              rules={[{ required: true, message: t("Please select industry") }]}
            >
              <Select
                placeholder={t("Select")}
                options={industryOptions}
                disabled={isDisabled}
              />
            </Form.Item>
            {industry === "Other" && (
              <Form.Item
                label={<span>{t("Please specify industry")}</span>}
                name="industryOther"
                rules={[
                  {
                    required: true,
                    message: t("Please specify industry"),
                  },
                ]}
              >
                <Input placeholder={t("Enter industry")} disabled={isDisabled} />
              </Form.Item>
            )}
            <Form.Item
              label={<span>{t("Value of Savings (USD)")} </span>}
              name="valueOfSavings"
              rules={[
                {
                  required: true,
                  message: t("Please select value of savings"),
                },
              ]}
            >
              <Select
                placeholder={t("Select")}
                options={valueOfSavingsOptions}
                disabled={isDisabled}
              />
            </Form.Item>
            <Form.Item
              label={<span>{t("Annual income (USD)")}</span>}
              name="annualIncome"
              rules={[
                { required: true, message: t("Please select annual income") },
              ]}
            >
              <Select
                placeholder={t("Select")}
                options={annualIncomeOptions}
                disabled={isDisabled}
              />
            </Form.Item>
            <Form.Item
                label={
                <span>
                  {t("Net Worth (Excluding the value of your primary residence)")}{" "}
                </span>
              }
              name="netWorth"
              rules={[{ required: true, message: t("Please select net worth") }]}
              className="md:col-span-2"
            >
              <Select
                placeholder={t("Select")}
                options={netWorthOptions}
                disabled={isDisabled}
              />
            </Form.Item>
            {renderSourceFundsAndDepositFields()}
          </div>
          <div className="border border-gray-200 rounded-lg p-6 mt-6">
            <h3 className="text-lg font-semibold mb-4">{t("Trading Experience")}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Form.Item
                label={<span>{t("How many times have you traded?")} </span>}
                name="tradingFrequency"
                rules={[
                  {
                    required: true,
                    message: t("Please select trading frequency"),
                  },
                ]}
              >
                <Radio.Group disabled={isDisabled}>
                  <Radio value="<10 times">{t("<10 times")}</Radio>
                  <Radio value="10-20 times">{t("10-20 times")}</Radio>
                  <Radio value=">20 times">{t(">20 times")}</Radio>
                </Radio.Group>
              </Form.Item>
              <Form.Item
                label={<span>{t("Understanding level")}</span>}
                name="understandingLevel"
                rules={[
                  {
                    required: true,
                    message: t("Please select understanding level"),
                  },
                ]}
              >
                <Radio.Group disabled={isDisabled}>
                  <Radio value="Limited">{t("Limited")}</Radio>
                  <Radio value="Good">{t("Good")}</Radio>
                  <Radio value="Excellent">{t("Excellent")}</Radio>
                </Radio.Group>
              </Form.Item>
            </div>
          </div>
        </div>
      );
    }

    if (employmentStatus === "Self Employed") {
      return (
        <div className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item
              label={<span>{t("Address of Employer")}</span>}
              name="employerAddress"
              rules={[
                {
                  required: true,
                  message: t("Please enter employer address"),
                },
              ]}
              className="md:col-span-2"
            >
              <TextArea
                rows={4}
                placeholder={t("Enter employer address")}
                disabled={isDisabled}
              />
            </Form.Item>
            <Form.Item
              label={<span>{t("Name of Company")}</span>}
              name="companyName"
              rules={[{ required: true, message: t("Please enter company name") }]}
            >
              <Input placeholder={t("Enter company name")} disabled={isDisabled} />
            </Form.Item>
            <Form.Item
              label={<span>{t("Industry")}</span>}
              name="industry"
              rules={[{ required: true, message: t("Please select industry") }]}
            >
              <Select
                placeholder={t("Select")}
                options={industryOptions}
                disabled={isDisabled}
              />
            </Form.Item>
            {industry === "Other" && (
              <Form.Item
                label={<span>{t("Please specify industry")}</span>}
                name="industryOther"
                rules={[
                  {
                    required: true,
                    message: t("Please specify industry"),
                  },
                ]}
              >
                <Input placeholder={t("Enter industry")} disabled={isDisabled} />
              </Form.Item>
            )}
            <Form.Item
              label={<span>{t("Value of Savings (USD)")} </span>}
              name="valueOfSavings"
              rules={[
                {
                  required: true,
                  message: t("Please select value of savings"),
                },
              ]}
            >
              <Select
                placeholder={t("Select")}
                options={valueOfSavingsOptions}
                disabled={isDisabled}
              />
            </Form.Item>
            <Form.Item
              label={<span>{t("Annual income (USD)")}</span>}
              name="annualIncome"
              rules={[
                { required: true, message: t("Please select annual income") },
              ]}
            >
              <Select
                placeholder={t("Select")}
                options={annualIncomeOptions}
                disabled={isDisabled}
              />
            </Form.Item>
            <Form.Item
                label={
                <span>
                  {t("Net Worth (Excluding the value of your primary residence)")}{" "}
                </span>
              }
              name="netWorth"
              rules={[{ required: true, message: t("Please select net worth") }]}
              className="md:col-span-2"
            >
              <Select
                placeholder={t("Select")}
                options={netWorthOptions}
                disabled={isDisabled}
              />
            </Form.Item>
            {renderSourceFundsAndDepositFields()}
          </div>
          <div className="border border-gray-200 rounded-lg p-6 mt-6">
            <h3 className="text-lg font-semibold mb-4">{t("Trading Experience")}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Form.Item
                label={<span>{t("How many times have you traded?")} </span>}
                name="tradingFrequency"
                rules={[
                  {
                    required: true,
                    message: t("Please select trading frequency"),
                  },
                ]}
              >
                <Radio.Group disabled={isDisabled}>
                  <Radio value="<10 times">{t("<10 times")}</Radio>
                  <Radio value="10-20 times">{t("10-20 times")}</Radio>
                  <Radio value=">20 times">{t(">20 times")}</Radio>
                </Radio.Group>
              </Form.Item>
              <Form.Item
                label={<span>{t("Understanding level")}</span>}
                name="understandingLevel"
                rules={[
                  {
                    required: true,
                    message: t("Please select understanding level"),
                  },
                ]}
              >
                <Radio.Group disabled={isDisabled}>
                  <Radio value="Limited">{t("Limited")}</Radio>
                  <Radio value="Good">{t("Good")}</Radio>
                  <Radio value="Excellent">{t("Excellent")}</Radio>
                </Radio.Group>
              </Form.Item>
            </div>
          </div>
        </div>
      );
    }

    if (employmentStatus === "Retired") {
      return (
        <div className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item
              label={<span>{t("Previous Industry")}</span>}
              name="previousIndustry"
              rules={[
                { required: true, message: t("Please select previous industry") },
              ]}
            >
              <Select
                placeholder={t("Select")}
                options={industryOptions}
                disabled={isDisabled}
              />
            </Form.Item>
            {previousIndustry === "Other" && (
              <Form.Item
                label={<span>{t("Please specify previous industry")}</span>}
                name="previousIndustryOther"
                rules={[
                  {
                    required: true,
                    message: t("Please specify previous industry"),
                  },
                ]}
              >
                <Input
                  placeholder={t("Enter previous industry")}
                  disabled={isDisabled}
                />
              </Form.Item>
            )}
            <Form.Item
              label={<span>{t("Occupation Role")}</span>}
              name="occupationRole"
              rules={[
                { required: true, message: t("Please select occupation role") },
              ]}
            >
              <Select
                placeholder={t("Select")}
                options={occupationRoleOptions}
                disabled={isDisabled}
              />
            </Form.Item>
            {occupationRole === "Others" && (
              <Form.Item
                label={<span>{t("Please specify occupation role")}</span>}
                name="occupationRoleOther"
                rules={[
                  {
                    required: true,
                    message: t("Please specify occupation role"),
                  },
                ]}
              >
                <Input
                  placeholder={t("Enter occupation role")}
                  disabled={isDisabled}
                />
              </Form.Item>
            )}
            <Form.Item
              label={<span>{t("Value of Savings (USD)")} </span>}
              name="valueOfSavings"
              rules={[
                {
                  required: true,
                  message: t("Please select value of savings"),
                },
              ]}
            >
              <Select
                placeholder={t("Select")}
                options={valueOfSavingsOptions}
                disabled={isDisabled}
              />
            </Form.Item>
            <Form.Item
              label={<span>{t("Annual income (USD)")}</span>}
              name="annualIncome"
              rules={[
                { required: true, message: t("Please select annual income") },
              ]}
            >
              <Select
                placeholder={t("Select")}
                options={annualIncomeOptions}
                disabled={isDisabled}
              />
            </Form.Item>
            <Form.Item
                label={
                <span>
                  {t("Net Worth (Excluding the value of your primary residence)")}{" "}
                </span>
              }
              name="netWorth"
              rules={[{ required: true, message: t("Please select net worth") }]}
              className="md:col-span-2"
            >
              <Select
                placeholder={t("Select")}
                options={netWorthOptions}
                disabled={isDisabled}
              />
            </Form.Item>
            {renderSourceFundsAndDepositFields()}
          </div>
          <div className="border border-gray-200 rounded-lg p-6 mt-6">
            <h3 className="text-lg font-semibold mb-4">{t("Trading Experience")}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Form.Item
                label={<span>{t("How many times have you traded?")} </span>}
                name="tradingFrequency"
                rules={[
                  {
                    required: true,
                    message: t("Please select trading frequency"),
                  },
                ]}
              >
                <Radio.Group disabled={isDisabled}>
                  <Radio value="<10 times">{t("<10 times")}</Radio>
                  <Radio value="10-20 times">{t("10-20 times")}</Radio>
                  <Radio value=">20 times">{t(">20 times")}</Radio>
                </Radio.Group>
              </Form.Item>
              <Form.Item
                label={<span>{t("Understanding level")}</span>}
                name="understandingLevel"
                rules={[
                  {
                    required: true,
                    message: t("Please select understanding level"),
                  },
                ]}
              >
                <Radio.Group disabled={isDisabled}>
                  <Radio value="Limited">{t("Limited")}</Radio>
                  <Radio value="Good">{t("Good")}</Radio>
                  <Radio value="Excellent">{t("Excellent")}</Radio>
                </Radio.Group>
              </Form.Item>
            </div>
          </div>
        </div>
      );
    }

    if (employmentStatus === "Not Employed") {
      return (
        <div className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item
              label={<span>{t("Annual income (USD)")}</span>}
              name="annualIncome"
              rules={[
                { required: true, message: t("Please select annual income") },
              ]}
            >
              <Select
                placeholder={t("Select")}
                options={annualIncomeOptions}
                disabled={isDisabled}
              />
            </Form.Item>
            <Form.Item
              label={<span>{t("Value of Savings (USD)")} </span>}
              name="valueOfSavings"
              rules={[
                {
                  required: true,
                  message: t("Please select value of savings"),
                },
              ]}
            >
              <Select
                placeholder={t("Select")}
                options={valueOfSavingsOptions}
                disabled={isDisabled}
              />
            </Form.Item>
            {renderSourceFundsAndDepositFields()}
            <Form.Item
                label={
                <span>
                  {t("Net Worth (Excluding the value of your primary residence)")}{" "}
                </span>
              }
              name="netWorth"
              rules={[{ required: true, message: t("Please select net worth") }]}
            >
              <Select
                placeholder={t("Select")}
                options={netWorthOptions}
                disabled={isDisabled}
              />
            </Form.Item>
          </div>
          <div className="border border-gray-200 rounded-lg p-6 mt-6">
            <h3 className="text-lg font-semibold mb-4">{t("Trading Experience")}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Form.Item
                label={<span>{t("How many times have you traded?")} </span>}
                name="tradingFrequency"
                rules={[
                  {
                    required: true,
                    message: t("Please select trading frequency"),
                  },
                ]}
              >
                <Radio.Group disabled={isDisabled}>
                  <Radio value="<10 times">{t("<10 times")}</Radio>
                  <Radio value="10-20 times">{t("10-20 times")}</Radio>
                  <Radio value=">20 times">{t(">20 times")}</Radio>
                </Radio.Group>
              </Form.Item>
              <Form.Item
                label={<span>{t("Understanding level")}</span>}
                name="understandingLevel"
                rules={[
                  {
                    required: true,
                    message: t("Please select understanding level"),
                  },
                ]}
              >
                <Radio.Group disabled={isDisabled}>
                  <Radio value="Limited">{t("Limited")}</Radio>
                  <Radio value="Good">{t("Good")}</Radio>
                  <Radio value="Excellent">{t("Excellent")}</Radio>
                </Radio.Group>
              </Form.Item>
            </div>
          </div>
        </div>
      );
    }

    if (employmentStatus === "Student") {
      return (
        <div className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item
              label={<span>{t("Institution")}</span>}
              name="institution"
              rules={[
                { required: true, message: t("Please enter institution name") },
              ]}
            >
              <Input
                placeholder={t("Enter institution name")}
                disabled={isDisabled}
              />
            </Form.Item>
            <Form.Item
              label={<span>{t("Subject of your study")}</span>}
              name="subjectOfStudy"
              rules={[
                { required: true, message: t("Please select subject of study") },
              ]}
            >
              <Select
                placeholder={t("Select")}
                options={subjectOfStudyOptions}
                disabled={isDisabled}
              />
            </Form.Item>
            {subjectOfStudy === "Others" && (
              <Form.Item
                label={<span>{t("Please specify subject of study")}</span>}
                name="subjectOfStudyOther"
                rules={[
                  {
                    required: true,
                    message: t("Please specify subject of study"),
                  },
                ]}
              >
                <Input
                  placeholder={t("Enter subject of study")}
                  disabled={isDisabled}
                />
              </Form.Item>
            )}
            <Form.Item
              label={<span>{t("Annual income (USD)")}</span>}
              name="annualIncome"
              rules={[
                { required: true, message: t("Please select annual income") },
              ]}
            >
              <Select
                placeholder={t("Select")}
                options={annualIncomeOptions}
                disabled={isDisabled}
              />
            </Form.Item>
            <Form.Item
              label={<span>{t("Value of Savings (USD)")} </span>}
              name="valueOfSavings"
              rules={[
                {
                  required: true,
                  message: t("Please select value of savings"),
                },
              ]}
            >
              <Select
                placeholder={t("Select")}
                options={valueOfSavingsOptions}
                disabled={isDisabled}
              />
            </Form.Item>
            {renderSourceFundsAndDepositFields()}
            <Form.Item
                label={
                <span>
                  {t("Net Worth (Excluding the value of your primary residence)")}{" "}
                </span>
              }
              name="netWorth"
              rules={[{ required: true, message: t("Please select net worth") }]}
            >
              <Select
                placeholder={t("Select")}
                options={netWorthOptions}
                disabled={isDisabled}
              />
            </Form.Item>
          </div>
          <div className="border border-gray-200 rounded-lg p-6 mt-6">
            <h3 className="text-lg font-semibold mb-4">{t("Trading Experience")}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Form.Item
                label={<span>{t("How many times have you traded?")} </span>}
                name="tradingFrequency"
                rules={[
                  {
                    required: true,
                    message: t("Please select trading frequency"),
                  },
                ]}
              >
                <Radio.Group disabled={isDisabled}>
                  <Radio value="<10 times">{t("<10 times")}</Radio>
                  <Radio value="10-20 times">{t("10-20 times")}</Radio>
                  <Radio value=">20 times">{t(">20 times")}</Radio>
                </Radio.Group>
              </Form.Item>
              <Form.Item
                label={<span>{t("Understanding level")}</span>}
                name="understandingLevel"
                rules={[
                  {
                    required: true,
                    message: t("Please select understanding level"),
                  },
                ]}
              >
                <Radio.Group disabled={isDisabled}>
                  <Radio value="Limited">{t("Limited")}</Radio>
                  <Radio value="Good">{t("Good")}</Radio>
                  <Radio value="Excellent">{t("Excellent")}</Radio>
                </Radio.Group>
              </Form.Item>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="mt-6 p-6 border border-gray-200 rounded-lg bg-gray-50">
        <p className="text-gray-600">
          {t("Form for")} <strong>{employmentStatus}</strong> {t("will be added here.")}
        </p>
      </div>
    );
  };

  if (isLoading) return <Loader />;

  return (
    <div className="max-w-5xl mx-auto p-6">
      <Form
        form={form}
        size="large"
        layout="vertical"
        autoComplete="off"
        disabled={isDisabled}
        onFinish={handleSubmit}
        initialValues={{
          employmentStatus: "Employed",
        }}
      >
        <div className="border border-gray-300 rounded-lg p-6">
          <h2 className="text-2xl font-semibold mb-6">{t("Financial Info")}</h2>
          <Form.Item
            label={t("Employment Status")}
            name="employmentStatus"
            rules={[
              { required: true, message: t("Please select employment status") },
            ]}
          >
            <Select
              placeholder={t("Select Employment Status")}
              onChange={handleEmploymentStatusChange}
              options={employmentStatusOptions}
              disabled={isDisabled}
            />
          </Form.Item>
          {renderEmploymentStatusForm()}
        </div>
        <div className="flex justify-end gap-4 mt-6">
          {onPrevious && (
            <Button onClick={onPrevious} size="large" disabled={false}>
              {t("Previous")}
            </Button>
          )}
          {isDisabled ? (
            <Button type="primary" size="large" onClick={onNext} disabled={false}>
              {t("Next")}
            </Button>
          ) : (
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              loading={isSubmitLoading}
              disabled={isSubmitLoading}
            >
              {isSubmitLoading ? t("Saving...") : t("Next")}
            </Button>
          )}
        </div>
      </Form>
    </div>
  );
};

export default FinancialInfo;
